// src/components/AboutSection.js
import React, { useRef, useEffect, useState } from 'react';
import { useTheme } from './ThemeContext';
import './AboutSection.css';

const descriptions = [
  {
    title: 'About Me',
    text: `Hello! I’m Saksham Prajapati, a Master’s student in Quantum Science and Technology at the Technical University of Munich. With a strong foundation in Physics and a minor in Computer Science, I enjoy exploring the intersection of theory, computation, and experimental research.

    My academic and project experiences range from spectroscopy of comets and data analysis to quantum hardware, simulation of quantum systems, and early exposure to quantum cryptography. I’ve worked on astrophysical data using NASA's IR telescope, automated Python-based tools for the American Physical Society, and analyzed education networks using R and visualization libraries.

    While I’ve developed a particular interest in quantum technologies, I’m still exploring my long-term path—whether it’s in hardware, quantum simulation, or applied quantum software. What drives me is the challenge of solving real problems with science and code.

    I’m always looking for exciting opportunities to grow, collaborate, and contribute to impactful quantum projects.`,
  },
  {
    title: 'Über mich',
    text: `Hallo! Ich bin Saksham Prajapati, Masterstudent im Studiengang Quantum Science and Technology an der Technischen Universität München. Mit einem soliden Hintergrund in Physik und einem Nebenfach in Informatik interessiere ich mich für die Schnittstelle von Theorie, Berechnung und experimenteller Forschung.

    Meine akademischen und praktischen Erfahrungen reichen von der Spektroskopie von Kometen und Datenanalyse bis hin zu quantentechnischer Hardware, Simulation quantenmechanischer Systeme und ersten Schritten in der Quantenkryptographie. Ich habe astronomische Daten mit einem NASA-Infrarotteleskop verarbeitet, Python-Tools für die American Physical Society automatisiert und Netzwerkdaten mit R visualisiert und analysiert.

    Obwohl ich ein besonderes Interesse an Quantentechnologien entwickelt habe, bin ich noch offen für verschiedene Karrierewege – sei es in der Hardware, der Simulation oder der angewandten Quantensoftware. Was mich antreibt, ist die Herausforderung, reale Probleme mit Wissenschaft und Code zu lösen.

    Ich bin stets auf der Suche nach spannenden Möglichkeiten, um zu lernen, gemeinsam zu arbeiten und einen Beitrag zu innovativen Quantenprojekten zu leisten.`,
  },
  {
    title: 'मेरो बारेमा',
    text: `नमस्ते! म सक्षम प्रजापति, हाल टेक्निकल युनिभर्सिटी अफ म्युनिख मा Quantum Science and Technology मा मास्टर्स गर्दैछु। मैले भौतिकशास्त्रमा स्नातक गर्दै गर्दा कम्प्युटर साइन्स मा साइड सब्जेक्ट लिएको थिएँ, र अहिले म सिद्धान्त, कम्प्युटेसन, र प्रयोगात्मक अनुसन्धानको संगममा काम गर्न रुचि राख्छु।

    मेरो अध्ययन र परियोजनाहरूले मलाई धूमकेतुको स्पेक्ट्रोस्कोपी, डाटा विश्लेषण, क्वान्टम हार्डवेयर, क्वान्टम प्रणालीहरूको सिमुलेसन, र प्रारम्भिक क्वान्टम क्रिप्टोग्राफी सम्म पुर्‍याएका छन्। मैले NASA को इन्फ्रारेड टेलिस्कोप प्रयोग गरेर डाटा संकलन गरेको छु, Python मा टूलहरू बनाएको छु, र R प्रयोग गरेर सामाजिक सञ्जाल विश्लेषण गरेको छु।

    यद्यपि मलाई क्वान्टम प्रविधिप्रति गहिरो रुचि छ, म अझै आफ्नो करियरका लागि विभिन्न सम्भावनाहरू खुला राखेको छु—जसमा हार्डवेयर, सिमुलेसन, वा अनुप्रयोगमुखी क्वान्टम सफ्टवेयर समावेश हुन सक्छ। मलाई प्रेरित गर्ने कुरा भनेको विज्ञान र कोडको माध्यमबाट वास्तविक समस्याहरू समाधान गर्नु हो।

    म नयाँ कुरा सिक्न, सहकार्य गर्न र अर्थपूर्ण क्वान्टम प्रोजेक्टहरूमा योगदान दिन सधैं तयार छु।`,
  },
];

export default function AboutSection() {
  const scrollRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const { setIsDark } = useTheme();

  useEffect(() => {
    const container = scrollRef.current;

    const handleScroll = () => {
      if (!container) return;
      const scrollLeft = container.scrollLeft;
      const width = window.innerWidth;
      const index = Math.round(scrollLeft / width) % descriptions.length;
      setActiveIndex(index);
    };

    container.addEventListener('scroll', handleScroll);

    const autoScrollInterval = setInterval(() => {
      if (!container) return;
      const width = window.innerWidth;
      const nextIndex = (activeIndex + 1) % descriptions.length;
      container.scrollTo({
        left: nextIndex * width,
        behavior: 'smooth',
      });
    }, 10000);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearInterval(autoScrollInterval);
    };
  }, [activeIndex]);

  useEffect(() => {
    const isDark = activeIndex % 2 !== 0;
    setIsDark(isDark);
  }, [activeIndex, setIsDark]);

  const isDark = activeIndex % 2 !== 0;

  return (
    <section className="about-carousel-section">
  <div className="about-carousel" ref={scrollRef}>
    {descriptions.map((_, idx) => (
      <div
        key={idx}
        className={`about-slide ${idx % 2 === 0 ? 'white-bg' : 'dark-bg'}`}
      />
    ))}
  </div>

  <div className="about-text-overlay">
    <h2 className={`about-title ${isDark ? 'light' : 'dark'}`}>
      {descriptions[activeIndex].title}
    </h2>
    <div className={`about-description ${isDark ? 'light-text' : 'dark-text'}`}>
      {descriptions[activeIndex].text.split('\n\n').map((para, index) => (
        <p key={index} style={{ marginBottom: '1rem' }}>{para}</p>
      ))}
    </div>
  </div>
</section>

  );
}